import { Grid } from '@material-ui/core';
import { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { useNavigate } from 'react-router-dom';

import './skuList.scss';

import { ReactComponent as AddCircle } from 'design_system/src/static/icons/add-circle.svg';
import { ReactComponent as Trash } from 'design_system/src/static/icons/trash.svg';

import { RequireRole } from 'components/layouts/main/sidebar/Sidebar';
import {
	Button,
	ListTable,
	Pager,
	Select,
	SideModal,
	Typography,
} from 'design_system/src';
import useFindAllParams from 'hooks/useFindAllParams';
import useProviders from 'hooks/useProviders';
import { getBrandsWithModels } from 'models/inventory/controllers/brand.controller';
import { Roles } from 'models/ozonio/enums/ozonio.enum';
import { getSkus, updateSku } from 'models/sku/controllers/sku.controller';
import {
	Sku,
	SkuSortType,
	SkuStatusEnum,
} from 'models/sku/interfaces/sku.interface';
import { SkuVariant } from 'models/sku-variant/interfaces/sku-variant.interface';
import { toasterOptions } from 'models/shared/enums/toaster.enum';
import { toasterNotification } from 'models/shared/helpers/toasterNotification';
import { getVehicleDetails } from 'models/vehicle-detail/controllers/vehicle-detail.controller';
import motoLoading from 'static/icons/loading_moto.gif';
import SearchBar from '../../components/hocs/searchBar/searchBar';
import CreateBrandModal from '../inventory/createBrand/CreateBrandModal';
import SkuDetails from '../sku-details/SkuDetails';
import CardSku from './CardSku';
import { isVehicleDetailComplete, skuListColumns } from './constants';

interface BrandOption {
	label: string;
	value: string;
	models: { label: string; value: string }[];
}

const CLEAR_BRAND_OPTION = { label: 'Marca', value: '' };
const CLEAR_MODEL_OPTION = { label: 'Modelo', value: '' };
const CLEAR_STATUS_OPTION = { label: 'Estado', value: '' };
const CLEAR_PROVIDER_OPTION = { label: 'Todos los proveedores', value: '' };

const STATUS_OPTIONS = [
	{ label: 'Activo', value: SkuStatusEnum.ACTIVE },
	{ label: 'Inactivo', value: SkuStatusEnum.INACTIVE },
];

const SORT_OPTIONS = [
	{ label: 'SKU A-Z', value: SkuSortType.codeAsc },
	{ label: 'SKU Z-A', value: SkuSortType.codeDesc },
	{ label: 'Precio menor a mayor', value: SkuSortType.priceAsc },
	{ label: 'Precio mayor a menor', value: SkuSortType.priceDesc },
	{
		label: 'Creados más recientes',
		value: SkuSortType.createdAtNewer,
	},
	{
		label: 'Creados más antiguos',
		value: SkuSortType.createdAtOlder,
	},
	{
		label: 'Modificados más recientes',
		value: SkuSortType.updatedAtNewer,
	},
	{
		label: 'Modificados más antiguos',
		value: SkuSortType.updatedAtOlder,
	},
];

interface SkuListProps {
	onClick?: (sku: Sku, variant: SkuVariant) => void;
	selectedSkuId?: string;
	selectedVariantId?: string;
}

function SkuList({ onClick, selectedSkuId, selectedVariantId }: SkuListProps) {
	const navigate = useNavigate();
	const {
		findAllParams: { data, currentPage, pages, loading, query, count },
		setData,
		setCurrentPage,
		setPages,
		setLoading,
		setQuery,
		setCount,
	} = useFindAllParams<Sku, never>();
	const [skusWithDetails, setSkusWithDetails] = useState<Set<string>>(
		new Set(),
	);
	const [isMounted, setIsMounted] = useState(false);
	const [openCreate, setOpenCreate] = useState(false);
	const [openCreateBrand, setOpenCreateBrand] = useState(false);
	const [brandOptions, setBrandOptions] = useState<BrandOption[]>([]);
	const [selectedBrand, setSelectedBrand] = useState('');
	const [selectedModel, setSelectedModel] = useState('');
	const [selectedStatus, setSelectedStatus] = useState<SkuStatusEnum | ''>(
		onClick ? SkuStatusEnum.ACTIVE : '',
	);
	const providerOptions = useProviders();
	const [selectedProvider, setSelectedProvider] = useState('');
	const [selectedSort, setSelectedSort] = useState<SkuSortType>(
		SkuSortType.createdAtNewer,
	);
	const subview = !!onClick;

	const requestData = () => {
		setLoading(true);
		Promise.all([
			getSkus({
				brand: selectedBrand || undefined,
				model: selectedModel || undefined,
				status: selectedStatus || undefined,
				provider: selectedProvider || undefined,
				sort: selectedSort || undefined,
				searchText: query || undefined,
				page: currentPage,
				limit: 30,
			}),
			getVehicleDetails(),
		])
			.then(([result, details]) => {
				setData(result?.data ?? []);
				setPages(result?.pages ?? 0);
				setCount(result?.count ?? 0);
				setSkusWithDetails(
					new Set(
						(details ?? []).filter(isVehicleDetailComplete).map((d) => d.sku),
					),
				);
				setLoading(false);
			})
			.catch(() => {
				setLoading(false);
				setData([]);
			});
	};

	useEffect(() => {
		ReactGA.event('VIEW_Panda_sku_list', {
			category: 'Panda',
			label: 'page view to the SKU list view',
		});
		getBrandsWithModels().then((res) => {
			if (res) {
				const mapped = res
					.map((brand) => ({
						label: brand.name,
						value: brand._id,
						models: (brand.models ?? []).map((model) => ({
							label: model.name,
							value: model._id,
						})),
					}))
					.sort((a, b) => a.label.localeCompare(b.label));
				setBrandOptions(mapped);
			}
		});

		setIsMounted(true);
	}, []);

	useEffect(() => {
		if (!isMounted) return;
		if (currentPage === 1) {
			requestData();
		} else {
			setCurrentPage(1);
		}
	}, [
		isMounted,
		selectedBrand,
		selectedModel,
		selectedStatus,
		selectedProvider,
		selectedSort,
	]);

	useEffect(() => {
		if (!isMounted) return;
		requestData();
	}, [currentPage]);

	const modelOptions = selectedBrand
		? brandOptions.find((brand) => brand.value === selectedBrand)?.models ?? []
		: brandOptions.flatMap((brand) => brand.models);

	const handleSearch = (event: any) => {
		event.preventDefault();
		if (currentPage === 1) {
			requestData();
		} else {
			setCurrentPage(1);
		}
	};

	const handleBrandChange = (value: string) => {
		setSelectedBrand(value);
		setSelectedModel('');
	};

	const hasActiveFilters = !!(
		selectedBrand ||
		selectedModel ||
		selectedStatus ||
		selectedProvider
	);

	const cleanFilter = () => {
		setSelectedBrand('');
		setSelectedModel('');
		setSelectedProvider('');
		setSelectedStatus('');
	};

	const handleToggleStatus = (sku: Sku) => {
		const newStatus =
			sku.status === SkuStatusEnum.INACTIVE
				? SkuStatusEnum.ACTIVE
				: SkuStatusEnum.INACTIVE;

		updateSku(sku._id, { status: newStatus })
			.then(() => {
				toasterNotification({
					msg:
						newStatus === SkuStatusEnum.ACTIVE
							? 'SKU activado en catálogo'
							: 'SKU desactivado del catálogo',
					toasterType: toasterOptions.success,
					style: 'light',
				});
				requestData();
			})
			.catch(() => {
				toasterNotification({
					msg: 'No se pudo actualizar el estado del SKU',
					toasterType: toasterOptions.error,
					style: 'light',
				});
			});
	};

	const renderBody = () => {
		if (loading) {
			return (
				<div className="flex_center_col dim_100_per">
					<Typography
						weight="600"
						scale="heading4"
						textColor="neutral_800"
						className="m_b_xs"
					>
						Cargando SKUs...
					</Typography>
					<img src={motoLoading} alt="" className="h_xxxl" />
				</div>
			);
		}
		if (data.length) {
			if (subview) {
				return (
					<Grid
						container
						direction="row"
						spacing={3}
						className="h_100_per p_t_md spaced"
					>
						{data.map((sku) => (
							<Grid item md={6} key={sku._id}>
								<CardSku
									sku={sku}
									onClick={
										onClick as (selected: Sku, variant: SkuVariant) => void
									}
									selected={sku._id === selectedSkuId}
									selectedVariantId={
										sku._id === selectedSkuId ? selectedVariantId : undefined
									}
								/>
							</Grid>
						))}
					</Grid>
				);
			}
			return (
				<ListTable
					data={data}
					onRowClick={(item) => navigate(`/sku/${item.code}`)}
					canSelect={false}
					rowClassName="br_none"
					hideHeader
					className="m_t_md"
					cols={skuListColumns(skusWithDetails, handleToggleStatus)}
				/>
			);
		}
		return (
			<div className="flex_center dim_100_per">
				<Typography
					scale="heading4"
					weight="600"
					className="text_neutral_900 m_xl text_center"
				>
					{query
						? 'Tus filtros no generaron resultados'
						: 'No hay SKUs creados'}
				</Typography>
			</div>
		);
	};

	return (
		<div
			className={`dim_100_per flex_content_col ${
				subview ? '' : 'shadow_hard inner_container bg_neutral_400'
			}`}
		>
			<div className="flex_header">
				<div
					className={
						subview ? 'p_t_xs' : 'bg_neutral_0 shadow_hard p_t_xs p_b_md p_x_lg'
					}
				>
					<div className="display_flex flex_gap_sm flex_align_center">
						{subview ? null : (
							<Typography scale="heading3" weight="600" textColor="neutral_900">
								SKU
							</Typography>
						)}
						<SearchBar
							sortOptions={SORT_OPTIONS}
							sortValue={selectedSort}
							setsortValue={(value: string) =>
								setSelectedSort(value as SkuSortType)
							}
							handleSearch={handleSearch}
							query={query}
							setQuery={setQuery}
							miniSearchButton={subview}
							subview={subview}
							className="flex_grow_1"
						/>
					</div>
					<div className="display_flex flex_align_center flex_wrap flex_gap_sm">
						<Select
							fixedPlaceholder
							variant="default"
							placeholder="Marca"
							options={[...brandOptions, CLEAR_BRAND_OPTION]}
							value={selectedBrand}
							onChange={(e: any) => handleBrandChange(e.target.value)}
						/>
						<Select
							fixedPlaceholder
							variant="default"
							placeholder="Modelo"
							options={[...modelOptions, CLEAR_MODEL_OPTION]}
							value={selectedModel}
							onChange={(e: any) => setSelectedModel(e.target.value)}
						/>
						<Select
							fixedPlaceholder
							variant="default"
							placeholder="Estado"
							options={[...STATUS_OPTIONS, CLEAR_STATUS_OPTION]}
							value={selectedStatus}
							onChange={(e: any) => setSelectedStatus(e.target.value)}
						/>
						<Select
							fixedPlaceholder
							variant="default"
							placeholder="Proveedor"
							options={[
								...providerOptions.map((p) => ({
									label: p.name,
									value: p._id,
								})),
								CLEAR_PROVIDER_OPTION,
							]}
							value={selectedProvider}
							onChange={(e: any) => setSelectedProvider(e.target.value)}
						/>
						{hasActiveFilters && (
							<Button
								variant="ghost"
								scale="small"
								icon={<Trash />}
								onClick={() => cleanFilter()}
								color="primary"
							>
								Limpiar Filtro
							</Button>
						)}
					</div>
				</div>
			</div>

			<div className={`flex_body overflow_x_hidden ${subview ? '' : 'p_x_lg'}`}>
				{renderBody()}
			</div>

			<div
				className={`display_flex flex_align_center flex_justify_between flex_footer ${
					subview ? '' : 'bg_neutral_0 p_x_xxxl p_y_md'
				}`}
			>
				{subview ? (
					<div />
				) : (
					<div className="display_flex flex_gap_sm flex_align_center">
						<RequireRole
							authRoles={[Roles.ADMIN, Roles.INVENTORY_MODEL_MANAGER]}
						>
							<Button
								scale="small"
								icon={<AddCircle />}
								className="w_200_px"
								onClick={() => setOpenCreate(true)}
							>
								Añadir SKU
							</Button>
						</RequireRole>
						<RequireRole
							authRoles={[
								Roles.ADMIN,
								Roles.INVENTORY_MANAGER,
								Roles.INVENTORY_PRICE_MANAGER,
							]}
						>
							<Button
								variant="outline"
								scale="small"
								className="w_200_px"
								icon={<AddCircle />}
								onClick={() => setOpenCreateBrand(true)}
							>
								Crear marca
							</Button>
						</RequireRole>
					</div>
				)}
				<Pager
					totalPages={pages ?? 0}
					page={currentPage}
					setPage={setCurrentPage}
					count={count}
					className="w_fit"
				/>
			</div>

			{!subview && (
				<SideModal
					open={openCreate}
					fullOpen={false}
					setFullOpen={setOpenCreate}
					setOpen={setOpenCreate}
					size="medium"
				>
					{openCreate && (
						<SkuDetails
							onClose={(saved) => {
								setOpenCreate(false);
								if (saved) requestData();
							}}
						/>
					)}
				</SideModal>
			)}
			{!subview && (
				<CreateBrandModal
					open={openCreateBrand}
					setOpen={setOpenCreateBrand}
					onCreated={() => getBrandsWithModels(true)}
				/>
			)}
		</div>
	);
}

export default SkuList;
